import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StickableTanstackTable } from 'components/common/Table/StickableTanstackTable';
import {
  getDefaultColumnDescription,
  getStatusDesColumn
} from 'components/TableColumn/TableColumn';
import { Link } from 'react-router-dom';
import styles from './creativeBindStatusPage.module.scss';
import i18n from 'i18n';
import { useCallAPI } from 'hooks/useCallAPI';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import {
  FilterMenuTab,
  FilterMenuTabConfig
} from 'components/common/FilterMenuTab/FilterMenuTab';
import { lowerCase, startCase, uniq } from 'lodash';
import { DefaultCreativeManager } from 'core/creative/CreativeManager';
import {
  CampaignBannerMapState,
  CreativeBindingCampaign
} from 'core/creative/Creative';
import { PmpStatus } from 'core/pmp/Pmp';
import moment from 'moment';
import { getEffectiveStatusDefaultColor } from 'components/Status/Status';

const COLUMNS = {
  ID: 'id',
  NAME: 'name',
  EFFECTIVE_STATUS: 'effectiveStatus',
  BIND_STATE: 'state'
};

const creativeManager = new DefaultCreativeManager();

export const CreativeBindStatusPage: React.FC<{
  creativeId: number;
  onClose: () => void;
}> = ({ creativeId, onClose }) => {
  const [campaigns, setCampaigns] = useState<CreativeBindingCampaign[]>([]);
  const { loading, callAPIs } = useCallAPI();
  const [selectedEffectiveStatusFilter, setSelectedEffectiveStatusFilter] =
    useState<string[]>([]);
  const [selectedBindStatusFilter, setSelectedBindStatusFilter] = useState<
    string[]
  >([]);

  const getEffectiveStatusData = useCallback(
    (campaign: CreativeBindingCampaign) => {
      let des = startCase(lowerCase(campaign.effectiveStatus));
      let color = getEffectiveStatusDefaultColor(campaign.effectiveStatus);
      let extraInfo;
      const now = moment();
      const pmp = campaign.pmp;
      const oriPmp = campaign.originPmp;
      const pmpIsEnd = pmp && moment(pmp.endTime).isBefore(now);
      const pmpIsInactive = pmp && pmp.status === PmpStatus.INACTIVE;
      const pmpIsExpired = pmp && pmp.status === PmpStatus.EXPIRED;
      switch (campaign.effectiveStatus) {
        case 'PARTIAL_STORE_NUMBER_INVALID':
          des = i18n.t('campaignList.labels.partialStoreNumberInvalid');
          color = 'warning';
          break;
        case 'ALL_STORE_NUMBER_INVALID':
          des = i18n.t('campaignList.labels.allStoreNumberInvalid');
          color = 'danger';
          break;
        case 'PAUSED':
        case 'CAMPAIGN_GROUP_PAUSED':
          if (campaign.budget - campaign.spent > 0) {
            des = i18n.t('campaignList.labels.budgetRemainState');
            color = 'danger';
          } else {
            des = i18n.t('campaignList.labels.deactivateState');
            color = 'black';
          }
          if (campaign.effectiveStatus === 'CAMPAIGN_GROUP_PAUSED') {
            extraInfo = 'Campaign Group Paused';
          }
          if (pmpIsInactive) {
            des = i18n.t('campaignList.labels.pmpInactiveState');
            color = 'black';
          } else if (pmpIsExpired) {
            des = i18n.t('campaignList.labels.pmpExpiredState');
            color = 'danger';
          }
          break;
        case 'DELETED':
        case 'ARCHIVED':
          des = i18n.t('campaignList.labels.deleteState');
          color = 'danger';
          break;
        case 'ACTIVE':
          if (oriPmp) {
            des = i18n.t('campaignList.labels.pmpChangedState');
            color = 'danger';
            break;
          }
          if (moment(campaign.startDate).isAfter(now)) {
            des = i18n.t('campaignList.labels.notStartState');
            color = 'light';
          } else if (moment(campaign.endDate).isBefore(now)) {
            des = i18n.t('campaignList.labels.endState');
            color = 'light';
          } else if (campaign.enabledCreativeCount === 0) {
            des = i18n.t('campaignList.labels.noCreativesState');
            color = 'black';
          } else {
            des = i18n.t('campaignList.labels.activateState');
          }
          if (pmpIsEnd) {
            des = i18n.t('campaignList.labels.pmpEndState');
            color = 'light';
          }
          break;
        default:
          break;
      }

      return {
        des,
        color,
        extraInfo
      };
    },
    []
  );

  const getBindStateData = useCallback((campaign: CreativeBindingCampaign) => {
    if (campaign.state === CampaignBannerMapState.ENABLE) {
      return {
        des: i18n.t('creativeBindStatusPage.labels.active'),
        color: 'success'
      };
    }

    return {
      des: i18n.t('creativeBindStatusPage.labels.inactive'),
      color: 'black'
    };
  }, []);

  useEffect(() => {
    callAPIs(
      [() => creativeManager.getCreativeBindingStatus(creativeId)],
      setCampaigns
    );
  }, [creativeId, callAPIs]);

  const bindStates = useMemo(() => {
    return uniq(campaigns.map((campaign) => getBindStateData(campaign).des));
  }, [campaigns, getBindStateData]);

  const effectiveStatus = useMemo(() => {
    return uniq(
      campaigns.map((campaign) => getEffectiveStatusData(campaign).des)
    );
  }, [campaigns, getEffectiveStatusData]);

  const filterMenuTabConfigs: FilterMenuTabConfig[] = useMemo(() => {
    return [
      {
        filterType: i18n.t('l2ObjectList.labels.deliveryFilter'),
        menuTitle: i18n.t('l2ObjectList.labels.deliveryFilterMenuTitle'),
        tag: i18n.t('l2ObjectList.labels.deliveryTag'),
        selectedValues: selectedEffectiveStatusFilter,
        options: effectiveStatus,
        applyMethod: setSelectedEffectiveStatusFilter
      },
      {
        filterType: i18n.t('l2ObjectList.labels.statusFilter'),
        menuTitle: i18n.t('l2ObjectList.labels.statusFilterMenuTitle'),
        tag: i18n.t('l2ObjectList.labels.statusTag'),
        selectedValues: selectedBindStatusFilter,
        options: bindStates,
        applyMethod: setSelectedBindStatusFilter
      }
    ];
  }, [
    selectedEffectiveStatusFilter,
    selectedBindStatusFilter,
    bindStates,
    effectiveStatus
  ]);

  const idFormatter = useCallback(
    (_1, campaign: CreativeBindingCampaign) => {
      return (
        <Link
          to={`/campaigns/${campaign.id}/redirectToDetail?l1ObjectId=${campaign.l1ObjectId}`}
          onClick={onClose}
        >
          {campaign.id}
        </Link>
      );
    },
    [onClose]
  );

  const columns = useMemo(
    () => [
      {
        ...getDefaultColumnDescription(
          COLUMNS.ID,
          i18n.t('creativeBindStatusPage.headers.id')
        ),
        formatter: idFormatter
      },
      getDefaultColumnDescription(
        COLUMNS.NAME,
        i18n.t('creativeBindStatusPage.headers.name')
      ),
      getStatusDesColumn(
        getDefaultColumnDescription(
          COLUMNS.EFFECTIVE_STATUS,
          i18n.t('creativeBindStatusPage.headers.effectiveStatus')
        ),
        (record) => getEffectiveStatusData(record)
      ),
      getStatusDesColumn(
        getDefaultColumnDescription(
          COLUMNS.BIND_STATE,
          i18n.t('creativeBindStatusPage.headers.bindState')
        ),
        (record) => getBindStateData(record)
      )
    ],
    [idFormatter, getEffectiveStatusData, getBindStateData]
  );

  return (
    <div className={styles.creativeBindManagePage}>
      {loading && <LoadingIndicator />}
      <div className={styles.filterContainer}>
        <FilterMenuTab filterMenuTabConfigs={filterMenuTabConfigs} />
      </div>
      <div className={styles.tableContainer}>
        <StickableTanstackTable
          keyField={COLUMNS.ID}
          data={campaigns}
          columns={columns}
          noDataIndication={i18n.t('creativeBindStatusPage.labels.noData')}
          defaultSorted={[
            {
              dataField: COLUMNS.ID,
              order: 'desc'
            }
          ]}
        />
      </div>
    </div>
  );
};
